<script setup lang="ts">
/**
 * Desktop: render the outline in-flow.
 * Mobile layout (phone shell or a narrow window, ZW-276): host the same
 * content inside a modal flyout. `side="bottom"` is the Scene details sheet:
 * it opens at half height, the handle drags or taps it to full height, and a
 * Close button is always visible.
 */
import { computed, watch } from "vue";
import { just, nothing } from "../lib/maybe";
import { useTea } from "../lib/tea";
import ZwFlyout from "./ui/ZwFlyout.vue";
import {
  initialBottomSheetModel,
  updateBottomSheetFn,
  type BottomSheetCmd,
} from "./ui/ZwBottomSheet.update";

const props = defineProps<{
  mobile: boolean;
  open: boolean;
  side: "left" | "right" | "bottom";
  /** Accessible name for the drawer or sheet. */
  label: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

function runCmd(cmd: BottomSheetCmd): void {
  switch (cmd.kind) {
    case "close":
      emit("close");
      return;
  }
}

const { model: sheet, dispatch } = useTea(
  initialBottomSheetModel,
  updateBottomSheetFn,
  { runCmd: just(runCmd) },
);

// Every open starts at half height.
watch(
  () => props.open,
  (open) => {
    if (open) void dispatch({ type: "Reset" });
  },
);

const isSheet = computed(() => props.side === "bottom");

const sheetHeight = computed(() => {
  const base = sheet.value.expanded ? "100dvh" : "50dvh";
  const offset = sheet.value.dragOffset;
  return offset === 0 ? base : `calc(${base} - ${offset}px)`;
});

const handleLabel = computed(() =>
  sheet.value.expanded ? "Shrink panel" : "Expand panel",
);

function onHandlePointerDown(event: PointerEvent): void {
  const target = event.currentTarget;
  if (target instanceof HTMLElement) target.setPointerCapture(event.pointerId);
  void dispatch({ type: "DragStarted", y: event.clientY });
}

function onHandlePointerMove(event: PointerEvent): void {
  void dispatch({ type: "DragMoved", y: event.clientY });
}

function onHandlePointerUp(): void {
  void dispatch({ type: "DragEnded" });
}

function onHandleKeydown(event: KeyboardEvent): void {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  void dispatch({ type: "ToggleExpanded" });
}
</script>

<template>
  <ZwFlyout
    v-if="mobile && isSheet"
    :id="just(`zw-outline-drawer-${props.side}`)"
    :labelled-by="nothing"
    :open="open"
    side="bottom"
    :close-on-backdrop="true"
    :z-index="60"
    width="min(100%, 22rem)"
    :height="sheetHeight"
    :label="just(label)"
    :flush="true"
    :full-bleed="false"
    initial-focus=".zw-sheet-close"
    @close="emit('close')"
  >
    <div class="zw-sheet" :data-expanded="sheet.expanded ? 'true' : 'false'">
      <div class="zw-sheet-bar">
        <button
          type="button"
          class="zw-sheet-handle"
          :aria-label="handleLabel"
          :title="handleLabel"
          @pointerdown="onHandlePointerDown"
          @pointermove="onHandlePointerMove"
          @pointerup="onHandlePointerUp"
          @pointercancel="onHandlePointerUp"
          @keydown="onHandleKeydown"
        >
          <span class="zw-sheet-grip" aria-hidden="true" />
        </button>
        <button
          type="button"
          class="zw-sheet-close"
          :aria-label="`Close ${label.toLowerCase()}`"
          @click="emit('close')"
        >
          Close
        </button>
      </div>
      <div class="zw-sheet-body">
        <slot />
      </div>
    </div>
  </ZwFlyout>
  <ZwFlyout
    v-else-if="mobile"
    :id="just(`zw-outline-drawer-${props.side}`)"
    :labelled-by="nothing"
    :open="open"
    :side="props.side === 'right' ? 'right' : 'left'"
    :close-on-backdrop="true"
    :z-index="60"
    width="14.5rem"
    height="50dvh"
    :label="just(label)"
    :flush="true"
    :full-bleed="false"
    initial-focus=".zw-outline-hide, button:not([disabled])"
    @close="emit('close')"
  >
    <slot />
  </ZwFlyout>
  <!-- Single root so parent v-show / class attrs apply (bare <slot> is a fragment). -->
  <div v-else class="zw-outline-surface">
    <slot />
  </div>
</template>

<style scoped>
.zw-outline-surface {
  display: contents;
}

.zw-sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.zw-sheet-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  min-height: 2.75rem;
  padding: 0 var(--space-sm);
  border-bottom: 1px solid var(--line);
}

.zw-sheet-handle {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 100%;
  border: 0;
  background: transparent;
  cursor: grab;
  touch-action: none;
}

.zw-sheet-handle:active {
  cursor: grabbing;
}

.zw-sheet-grip {
  display: block;
  width: 2.5rem;
  height: 0.3rem;
  border-radius: 999px;
  background: color-mix(in oklch, var(--text-strong) 30%, transparent);
}

.zw-sheet-close {
  min-height: 2.25rem;
  padding: 0 var(--space-md);
  border: 1px solid var(--line);
  border-radius: 0.5rem;
  background: var(--bg);
  color: var(--text-strong);
  font: inherit;
  font-size: 0.9375rem;
  cursor: pointer;
}

.zw-sheet-handle:focus-visible,
.zw-sheet-close:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

.zw-sheet-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
}

.zw-sheet-body > :deep(*) {
  min-height: 100%;
}
</style>
